import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import {useTranslation} from 'react-i18next'
import type {QrGradientStop} from '../../../common/utils/qrRenderer'

interface QrGradientBarProps {
  stops: QrGradientStop[]
  onChange: (stops: QrGradientStop[]) => void
}

const MARKER_HIT_RADIUS = 0.04

const buildGradientCSS = (stops: QrGradientStop[]): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  return `linear-gradient(90deg, ${sorted.map((s) => `${s.color} ${s.position * 100}%`).join(', ')})`
}

const interpolateColorAt = (stops: QrGradientStop[], pos: number): string => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  if (sorted.length === 0) return '#000000'
  if (pos <= sorted[0].position) return sorted[0].color
  if (pos >= sorted[sorted.length - 1].position) return sorted[sorted.length - 1].color
  for (let i = 0; i < sorted.length - 1; i++) {
    const left = sorted[i]
    const right = sorted[i + 1]
    if (pos >= left.position && pos <= right.position) {
      const t = (pos - left.position) / (right.position - left.position)
      const lR = parseInt(left.color.slice(1, 3), 16)
      const lG = parseInt(left.color.slice(3, 5), 16)
      const lB = parseInt(left.color.slice(5, 7), 16)
      const rR = parseInt(right.color.slice(1, 3), 16)
      const rG = parseInt(right.color.slice(3, 5), 16)
      const rB = parseInt(right.color.slice(5, 7), 16)
      const r = Math.round(lR + (rR - lR) * t)
      const g = Math.round(lG + (rG - lG) * t)
      const b = Math.round(lB + (rB - lB) * t)
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    }
  }
  return sorted[0].color
}

const generateStopId = (): string => `qs_${Math.random().toString(36).slice(2, 9)}`

const QrGradientBar = ({stops, onChange}: QrGradientBarProps) => {
  const {t} = useTranslation()
  const barRef = useRef<HTMLDivElement>(null)
  const draggingIdRef = useRef<string | null>(null)
  const [selectedStopId, setSelectedStopId] = useState<string>(stops[0]?.id ?? '')

  const selectedStop = stops.find((s) => s.id === selectedStopId) ?? stops[0] ?? null

  const positionFromX = useCallback((clientX: number): number => {
    if (!barRef.current) return 0
    const rect = barRef.current.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  }, [])

  const handleBarClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).dataset.marker) return
      const pos = positionFromX(e.clientX)
      if (stops.some((s) => Math.abs(s.position - pos) < MARKER_HIT_RADIUS)) return
      const newStop: QrGradientStop = {
        id: generateStopId(),
        color: interpolateColorAt(stops, pos),
        position: Math.round(pos * 1000) / 1000,
      }
      onChange([...stops, newStop])
      setSelectedStopId(newStop.id)
    },
    [stops, onChange, positionFromX],
  )

  const handleMarkerPointerDown = useCallback(
    (stopId: string, e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedStopId(stopId)
      draggingIdRef.current = stopId

      const handleMove = (ev: PointerEvent) => {
        const id = draggingIdRef.current
        if (!id) return
        const pos = positionFromX(ev.clientX)
        onChange(stops.map((s) => (s.id === id ? {...s, position: Math.round(pos * 1000) / 1000} : s)))
      }
      const handleUp = () => {
        draggingIdRef.current = null
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        window.removeEventListener('pointercancel', handleUp)
      }
      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
      window.addEventListener('pointercancel', handleUp)
    },
    [stops, onChange, positionFromX],
  )

  const handleStopColorChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!selectedStop) return
      onChange(stops.map((s) => (s.id === selectedStop.id ? {...s, color: e.target.value} : s)))
    },
    [stops, onChange, selectedStop],
  )

  const handleDeleteStop = useCallback(() => {
    if (!selectedStop || stops.length <= 2) return
    const next = stops.filter((s) => s.id !== selectedStop.id)
    onChange(next)
    setSelectedStopId(next[0]?.id ?? '')
  }, [stops, onChange, selectedStop])

  return (
    <div className="qrg-gradient">
      <div
        ref={barRef}
        className="qrg-gradient-bar"
        style={{background: buildGradientCSS(stops)}}
        onClick={handleBarClick}
      >
        {stops.map((stop) => (
          <div
            key={stop.id}
            data-marker="true"
            className={`qrg-gradient-marker${selectedStop?.id === stop.id ? ' is-selected' : ''}`}
            style={{left: `${stop.position * 100}%`, background: stop.color}}
            onPointerDown={(e) => handleMarkerPointerDown(stop.id, e)}
          />
        ))}
      </div>
      {selectedStop && (
        <div className="qrg-row">
          <label className="qrg-label">{t('qrGenerator.style.stopColor')}</label>
          <input type="color" className="qrg-color-input" value={selectedStop.color} onChange={handleStopColorChange} />
          {stops.length > 2 && (
            <button type="button" className="qrg-btn qrg-btn-ghost" onClick={handleDeleteStop}>
              {t('qrGenerator.style.removeStop')}
            </button>
          )}
        </div>
      )}
      <p className="qrg-hint">{t('qrGenerator.style.gradientTip')}</p>
    </div>
  )
}

export default QrGradientBar
